import React, { useState, useRef, useCallback } from 'react';
import { Mic, MicOff, X, Loader2, Volume2 } from 'lucide-react';
import { API, apiHeaders, uploadHeaders, Logo, ttsSpeak } from './chatUtils';

export default function TalkMode({ sessionId, onClose, onNewSession }) {
  const [status, setStatus] = useState('idle');
  const [lastUserText, setLastUserText] = useState('');
  const [lastAiText, setLastAiText] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);

  const statusText = {
    idle: 'Tap to speak',
    recording: 'Listening...',
    transcribing: 'Processing speech...',
    thinking: 'Thinking...',
    speaking: 'Speaking...',
  };

  const startRecording = useCallback(async () => {
    if (status !== 'idle') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) { setStatus('idle'); return; }

        setStatus('transcribing');
        try {
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');
          const sttRes = await fetch(`${API}/api/voice/stt`, {
            method: 'POST', headers: uploadHeaders(), body: formData,
          });
          if (!sttRes.ok) { setStatus('idle'); return; }
          const sttData = await sttRes.json();
          const userText = sttData.text;
          if (!userText?.trim()) { setStatus('idle'); return; }
          setLastUserText(userText);

          let sid = sessionId;
          if (!sid) {
            const sessRes = await fetch(`${API}/api/chat/sessions`, {
              method: 'POST', headers: apiHeaders(),
              body: JSON.stringify({ title: userText.slice(0, 50) }),
            });
            const sessData = await sessRes.json();
            sid = sessData.session_id;
            onNewSession?.(sessData);
          }

          setStatus('thinking');
          const chatRes = await fetch(`${API}/api/chat/send`, {
            method: 'POST', headers: apiHeaders(),
            body: JSON.stringify({ session_id: sid, message: userText }),
          });
          if (!chatRes.ok) { setStatus('idle'); return; }
          const chatData = await chatRes.json();
          setLastAiText(chatData.content);

          setStatus('speaking');
          const headers = apiHeaders();
          delete headers['Content-Type'];
          const audio = await ttsSpeak(chatData.content, headers);
          if (audio) {
            audioRef.current = audio;
            audio.onended = () => { setStatus('idle'); audioRef.current = null; };
            audio.onerror = () => { setStatus('idle'); audioRef.current = null; };
          } else {
            setStatus('idle');
          }
        } catch (e) {
          console.error('Talk mode error:', e);
          setStatus('idle');
        }
      };

      mediaRecorder.start();
      setStatus('recording');
    } catch {
      alert('Microphone access denied.');
    }
  }, [status, sessionId, onNewSession]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setStatus('idle');
  }, []);

  const handleMainAction = () => {
    if (status === 'recording') stopRecording();
    else if (status === 'speaking') stopSpeaking();
    else if (status === 'idle') startRecording();
  };

  const ringColor = status === 'recording' ? 'border-red-500 shadow-[0_0_40px_rgba(239,68,68,0.2)]'
    : status === 'speaking' ? 'border-[#10B981] shadow-[0_0_40px_rgba(16,185,129,0.2)]'
    : 'border-line-2';

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-app"
      data-testid="talk-mode">
      <button onClick={onClose}
        className="absolute top-5 right-5 p-3 rounded-full bg-overlay text-content-2 hover:bg-overlay-hover hover:text-content transition-all border border-line"
        data-testid="close-talk-btn">
        <X className="w-5 h-5" strokeWidth={1.5} />
      </button>

      <Logo size="lg" className="mb-8 opacity-80" />

      <div className="text-center mb-10 px-6 max-w-md">
        {lastUserText && (
          <p className="text-sm mb-3 text-content-3 font-sora">
            You: {lastUserText}
          </p>
        )}
        {lastAiText && (
          <p className="text-sm text-content-2 font-sora line-clamp-4 leading-relaxed">
            {lastAiText}
          </p>
        )}
      </div>

      <button onClick={handleMainAction}
        disabled={status === 'transcribing' || status === 'thinking'}
        className={`w-28 h-28 rounded-full border-2 flex items-center justify-center transition-all duration-500 active:scale-95
          ${ringColor} bg-overlay
          ${(status === 'transcribing' || status === 'thinking') ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105'}`}
        data-testid="talk-mic-btn">
        {status === 'transcribing' || status === 'thinking'
          ? <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
          : status === 'recording'
            ? <MicOff className="w-10 h-10 text-red-400" />
            : status === 'speaking'
              ? <Volume2 className="w-10 h-10 text-emerald-500" />
              : <Mic className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />
        }
      </button>

      <p className="mt-6 text-sm text-content-3 font-sora">
        {statusText[status]}
      </p>
    </div>
  );
}
